// frontend/src/ts/protocols/persistence/useWriteStatusStore.ts
import { create } from 'zustand'
import { PendingSummary, PendingWrite, WriteInfo } from './types'
import { getWriteStore } from './writeStore'

export interface DeadWrite {
  id: number
  stepId: string | null
  coalesceKey: string | null
  writeType: string
  error: string | null
  payload: Record<string, unknown> | null
  createdAt: number
  attemptCount: number
  lastAttemptAt: number | null
}

interface WriteStatusState {
  perWrite: Map<string, WriteInfo>
  pendingCount: number
  deadCount: number
  deadWrites: DeadWrite[]
  /**
   * Last error from the bootstrap hydration `getPendingWrites` call.
   * When set, deadCount may be > 0 but deadWrites is stale (likely
   * empty). The PendingWritesTable uses this to surface a manual
   * "Retry hydration" button.
   */
  hydrationError: string | null
  bootstrapped: boolean
  /**
   * Bumped on terminal enqueue failure so uncontrolled defaultValue/onBlur
   * fields remount and re-read the store (the DOM still shows the edit
   * that was never queued).
   */
  formResetEpoch: number

  bootstrap: (companyId: string, protocolId: string) => Promise<void>
  shutdown: () => void
  bumpFormResetEpoch: () => void
  refresh: (companyId: string, protocolId: string) => Promise<void>
  /**
   * Re-run the dead-row hydration without re-running summary() or
   * re-bootstrapping. Used by the PendingWritesTable's manual Retry
   * button when bootstrap hydration failed.
   */
  retryHydration: (companyId: string, protocolId: string) => Promise<void>
  markPending: (writeKey: string, opts?: { incrementAttemptCount?: boolean }) => void
  markPersisted: (writeKey: string) => void
  /** Undo a `markPending` when enqueue failed and no row exists. No-op if not pending. */
  clearPending: (writeKey: string) => void
  markDead: (write: PendingWrite, error: string) => void
  markRetried: (writeKey: string) => void
  clearDeadWrite: (id: number) => void
  /**
   * Overwrite counts + deadWrites from a write store. Called by the
   * coordinator's write paths (writeStep, writeProtocol, writeEvent,
   * writeEventDelete) after each successful `enqueue` so the JS state
   * reflects whatever the Store cascade actually did (supersede,
   * cross-writeType no-cascade, etc.) instead of guessing with
   * increment/decrement math.
   *
   * Does NOT touch perWrite — per-row state (status, attemptCount,
   * lastError) is still driven by markPending / markPersisted /
   * markDead / markRetried. The reconcile path is for the aggregate
   * counts and the dead-row list only.
   */
  reconcileFromWriteStore: (summary: PendingSummary, deadRows: PendingWrite[]) => void
}

function pendingWriteToDeadWrite(p: PendingWrite): DeadWrite {
  return {
    id: p.id,
    stepId: p.stepId,
    coalesceKey: p.coalesceKey,
    writeType: p.writeType,
    error: p.lastError,
    payload: p.payload ?? null,
    createdAt: p.createdAt,
    attemptCount: p.attemptCount,
    lastAttemptAt: p.lastAttemptAt,
  }
}

export const useWriteStatusStore = create<WriteStatusState>((set, get) => ({
  perWrite: new Map(),
  pendingCount: 0,
  deadCount: 0,
  deadWrites: [],
  hydrationError: null,
  bootstrapped: false,
  formResetEpoch: 0,

  bumpFormResetEpoch: () => {
    set((s) => ({formResetEpoch: s.formResetEpoch + 1}))
  },

  bootstrap: async (companyId, protocolId) => {
    if (get().bootstrapped) return
    const store = getWriteStore()
    const summary = await store.summary(companyId, protocolId)
    set({
      pendingCount: summary.pending,
      deadCount: summary.dead,
    })
    // Hydrate deadWrites from the durable queue. Without this, dead
    // rows from a prior session are invisible to the UI: the modal
    // won't auto-open and the queue viewer is empty.
    //
    // Hydration failures must NOT roll back the counts we already set
    // from `summary()` — otherwise the user sees a red Cloud icon
    // (deadCount > 0) but an empty modal (deadWrites = []).
    // If the queue fetch throws, we log it, leave deadWrites = [],
    // and expose a manual retry path so the user can re-trigger
    // hydration from the UI.
    let hydrationError: string | null = null
    let deadRows: PendingWrite[] = []
    try {
      deadRows = await store.getPendingWrites({
        companyId,
        protocolId,
        stateFilter: ['dead'],
        coalesceView: false,
      })
    } catch (e) {
      hydrationError = e instanceof Error ? e.message : String(e)
      console.error('[useWriteStatusStore] dead-row hydration failed', e)
    }
    set({
      deadWrites: deadRows.map(pendingWriteToDeadWrite),
      hydrationError,
    })
    // Hydrate perWrite with dead rows so subsequent markRetried()
    // calls correctly increment pendingCount. Without this, perWrite
    // is empty after a fresh mount and markRetried's `prev?.status ===
    // 'dead'` is false, so the PendingWritesTray doesn't reflect the
    // new pending count after Retry (user-initiated or auto).
    const perWrite = new Map(get().perWrite)
    const deadWritesHydrated: DeadWrite[] = []
    for (const row of deadRows) {
      const writeKey = row.coalesceKey ?? row.stepId
      deadWritesHydrated.push(pendingWriteToDeadWrite(row))
      if (writeKey && !perWrite.has(writeKey)) {
        perWrite.set(writeKey, {
          status: 'dead',
          attemptCount: row.attemptCount,
          lastError: row.lastError ?? undefined,
        })
      }
    }
    set({ perWrite, deadWrites: deadWritesHydrated, hydrationError })
    // Auto-retry every dead write so the drainer picks them up on its
    // next tick. The Dead Writes UI only surfaces when these retries
    // fail (the drainer's markDead call appends the row back to
    // deadWrites[] and bumps deadCount).
    //
    // If the dead rows are still in the queue when the user opens the
    // page, we don't want them sitting there invisible — they
    // represent real work the user did that hasn't reached the server.
    // Auto-retrying means the user sees the values in the local
    // store (merged from the dead row) and the server eventually
    // catches up.
    if (deadRows.length > 0) {
      let retriedIds: number[] = []
      try {
        retriedIds = await store.retryAllDead(companyId, protocolId)
      } catch (e) {
        console.error('[useWriteStatusStore] bootstrap auto-retry failed', e)
      }
      for (const id of retriedIds) {
        const dw = get().deadWrites.find((d) => d.id === id)
        const writeKey = dw?.coalesceKey ?? dw?.stepId ?? null
        if (writeKey) get().markRetried(writeKey)
        get().clearDeadWrite(id)
      }
      console.info(`[useWriteStatusStore] auto-retried ${retriedIds.length} dead write(s) on bootstrap`)
      // Re-fetch summary and dead rows to reflect the new state.
      // (The Rust transaction committed; the JS status store should
      // match what the queue actually has now.)
      try {
        const newSummary = await store.summary(companyId, protocolId)
        set({ pendingCount: newSummary.pending, deadCount: newSummary.dead })
        const remainingDead = await store.getPendingWrites({
          companyId,
          protocolId,
          stateFilter: ['dead'],
          coalesceView: false,
        })
        set({ deadWrites: remainingDead.map(pendingWriteToDeadWrite) })
      } catch (e) {
        console.error('[useWriteStatusStore] bootstrap post-retry re-fetch failed', e)
      }
    }
    set({ bootstrapped: true })
  },

  shutdown: () => {
    set({ perWrite: new Map(), pendingCount: 0, deadCount: 0, deadWrites: [], hydrationError: null, bootstrapped: false, formResetEpoch: 0 })
  },

  refresh: async (companyId, protocolId) => {
    const summary = await getWriteStore().summary(companyId, protocolId)
    set({
      pendingCount: summary.pending,
      deadCount: summary.dead,
    })
  },

  retryHydration: async (companyId, protocolId) => {
    if (!get().bootstrapped) return
    const store = getWriteStore()
    let hydrationError: string | null = null
    let deadRows: PendingWrite[] = []
    try {
      deadRows = await store.getPendingWrites({
        companyId,
        protocolId,
        stateFilter: ['dead'],
        coalesceView: false,
      })
    } catch (e) {
      hydrationError = e instanceof Error ? e.message : String(e)
      console.error('[useWriteStatusStore] retryHydration failed', e)
    }
    set({
      deadWrites: deadRows.map(pendingWriteToDeadWrite),
      hydrationError,
    })
  },

  markPending: (writeKey, opts) => {
    set((s) => {
      const next = new Map(s.perWrite)
      const prev = next.get(writeKey)
      const base = prev ?? { attemptCount: 0 }
      next.set(writeKey, {
        ...base,
        status: 'pending',
        attemptCount: opts?.incrementAttemptCount ? base.attemptCount + 1 : base.attemptCount,
      })
      return {
        perWrite: next,
        pendingCount: opts?.incrementAttemptCount ? s.pendingCount : s.pendingCount + 1,
      }
    })
  },

  markPersisted: (writeKey) => {
    set((s) => {
      const next = new Map(s.perWrite)
      next.delete(writeKey)
      return {
        perWrite: next,
        pendingCount: Math.max(0, s.pendingCount - 1),
      }
    })
  },

  clearPending: (writeKey) => {
    set((s) => {
      if (s.perWrite.get(writeKey)?.status !== 'pending') return {}
      const next = new Map(s.perWrite)
      next.delete(writeKey)
      return {
        perWrite: next,
        pendingCount: Math.max(0, s.pendingCount - 1),
      }
    })
  },

  markDead: (write, error) => {
    set((s) => {
      const writeKey = write.coalesceKey ?? write.stepId
      const next = new Map(s.perWrite)
      const alreadyDead = writeKey ? next.get(writeKey)?.status === 'dead' : false
      if (writeKey) {
        const prev = next.get(writeKey)
        next.set(writeKey, {
          ...(prev ?? { attemptCount: write.attemptCount }),
          status: 'dead',
          attemptCount: write.attemptCount,
          lastError: error,
        })
      }
      // De-dupe: if a row with this id was already in deadWrites
      // (e.g. hydrated from the queue on bootstrap, or this row was
      // already marked dead by a prior attempt), don't add a duplicate
      // or double-bump deadCount.
      const deadWrites = s.deadWrites.some((d) => d.id === write.id)
        ? s.deadWrites
        : [...s.deadWrites, pendingWriteToDeadWrite({ ...write, lastError: error })]
      return {
        perWrite: next,
        pendingCount: Math.max(0, s.pendingCount - 1),
        deadCount: alreadyDead ? s.deadCount : s.deadCount + 1,
        deadWrites,
      }
    })
  },

  markRetried: (writeKey) => {
    set((s) => {
      const next = new Map(s.perWrite)
      const prev = next.get(writeKey)
      const wasDead = prev?.status === 'dead'
      next.set(writeKey, { ...(prev ?? { attemptCount: 0 }), status: 'pending' })
      return {
        perWrite: next,
        pendingCount: wasDead ? s.pendingCount + 1 : s.pendingCount,
      }
    })
  },

  clearDeadWrite: (id) => {
    set((s) => ({ deadWrites: s.deadWrites.filter((d) => d.id !== id), deadCount: Math.max(0, s.deadCount - 1) }))
  },

  reconcileFromWriteStore: (summary, deadRows) => {
    set({
      pendingCount: summary.pending,
      deadCount: summary.dead,
      deadWrites: deadRows.map(pendingWriteToDeadWrite),
    })
  },
}))
